import React, { useCallback, useEffect, useRef, useState } from "react";
import { invoiceApi, printApi } from "../../api/lab";
import { useAuth } from "../../AuthProvider";
import { useApp } from "../../AppProvider";
import { Modal, Button, Badge, Section, ReasonModal, Loading } from "../../Components/UI/ui";
import { GATE, money, fmtDate, fmtDateTime, cap, openPdf } from "../../lib/lab";
import PaymentModal from "./PaymentModal";

const InvoiceDetail = ({ id, open, onClose, onChanged }) => {
  const { can } = useAuth();
  const { toast, defaultLayout, loadBranding } = useApp();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [reason, setReason] = useState(null); // { kind: 'void', paymentId } | { kind: 'cancel' }
  const [busy, setBusy] = useState(false);
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  const onChangedRef = useRef(onChanged); onChangedRef.current = onChanged;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try { setInv(await invoiceApi.get(id)); }
    catch (e) { toast.error(e); onCloseRef.current?.(); }
    finally { setLoading(false); }
  }, [id, toast]);

  useEffect(() => { if (open) { load(); loadBranding(); } }, [open, load, loadBranding]);

  const after = async () => { await load(); onChangedRef.current?.(); };

  const doReason = async (text) => {
    setBusy(true);
    try {
      if (reason.kind === "void") { await invoiceApi.voidPayment(inv._id, reason.paymentId, text); toast.success("Payment voided"); }
      else { await invoiceApi.cancel(inv._id, text); toast.success("Invoice cancelled"); }
      setReason(null);
      after();
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this invoice?")) return;
    setBusy(true);
    try { await invoiceApi.remove(inv._id); toast.success("Invoice deleted"); onChangedRef.current?.(); onCloseRef.current?.(); }
    catch (e) { toast.error(e); } finally { setBusy(false); }
  };

  const outstanding = inv ? inv.outstanding ?? inv.grandTotal - (inv.totalPaidAmount || 0) : 0;
  const livePayments = (inv?.paymentHistory || []).filter((p) => !p.voided);
  const canPay = inv && !["Paid", "Cancelled"].includes(inv.status) && can(...GATE.invoiceWrite);
  const admin = can(...GATE.billingAdmin);

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        size="lg"
        title={inv ? `Invoice ${inv.invoiceNumber}` : "Invoice"}
        footer={inv && (
          <>
            <span className="lab-row" style={{ marginRight: "auto", gap: 6 }}>
              <Button size="sm" onClick={() => openPdf(printApi.invoicePdfUrl(inv._id, { layout: defaultLayout }))} title="Opens the invoice with its current payment status — re-print after a payment to hand over a receipt">🖨 Print</Button>
              <Button size="sm" variant="ghost" onClick={() => openPdf(printApi.invoicePdfUrl(inv._id, { layout: defaultLayout, download: true }))}>Download</Button>
              {can(...GATE.deleteInvoice) && (inv.status === "Cancelled" || (inv.status === "Pending" && livePayments.length === 0)) && (
                <Button variant="danger" size="sm" loading={busy} onClick={remove}>Delete</Button>
              )}
            </span>
            {admin && inv.status !== "Cancelled" && (
              <Button variant="danger" disabled={livePayments.length > 0} title={livePayments.length ? "Void all payments first" : ""} onClick={() => setReason({ kind: "cancel" })}>Cancel invoice</Button>
            )}
            {canPay && <Button variant="primary" onClick={() => setPayOpen(true)}>Record payment</Button>}
          </>
        )}
      >
        {loading || !inv ? <Loading /> : (
          <>
            <div className="lab-row between">
              <div className="lab-row">
                <Badge kind={inv.status?.split(" ")[0]}>{inv.status}</Badge>
                {inv.isOverdue && inv.status !== "Paid" && inv.status !== "Cancelled" && <Badge kind="unpaid">Overdue</Badge>}
                {inv.labAppointmentId ? <span className="lab-muted">Booking-linked</span> : <span className="lab-muted">Walk-in</span>}
              </div>
              <div className="lab-muted">Issued {fmtDate(inv.date)}{inv.dueDate ? ` · due ${fmtDate(inv.dueDate)}` : ""}</div>
            </div>

            <div className="lab-grid cols-2">
              <Section title="Patient">
                <dl className="lab-kv">
                  <dt>Name</dt><dd>{inv.patientName}</dd>
                  <dt>Phone</dt><dd>{inv.patientPhone || "—"}</dd>
                  {inv.referral?.doctorName && (<><dt>Referred by</dt><dd>{inv.referral.doctorName}</dd></>)}
                  {inv.notes && (<><dt>Notes</dt><dd>{inv.notes}</dd></>)}
                </dl>
              </Section>
              <Section title="Totals">
                <div className="lab-total"><span>Subtotal</span><span>{money(inv.subTotal)}</span></div>
                <div className="lab-total"><span>Discount</span><span>− {money(inv.discountTotal)}</span></div>
                <div className="lab-total"><span>Tax</span><span>{money(inv.taxTotal)}</span></div>
                <div className="lab-total grand"><span>Grand total</span><span>{money(inv.grandTotal)}</span></div>
                <div className="lab-total"><span>Paid</span><span>{money(inv.totalPaidAmount || 0)}</span></div>
                <div className="lab-total" style={{ fontWeight: 600, color: outstanding > 0 ? "#d64545" : "#2e9e5b" }}><span>Outstanding</span><span>{money(outstanding)}</span></div>
              </Section>
            </div>

            <Section title="Items">
              <table className="lab-lines">
                <thead><tr><th>Item</th><th className="num">Qty</th><th className="num">Price</th><th className="num">Disc</th><th className="num">Tax</th><th className="num">Total</th></tr></thead>
                <tbody>
                  {(inv.expenseTable || []).map((l, i) => (
                    <tr key={i}>
                      <td>{l.name} <span className="lab-muted">· {cap(l.kind)}</span></td>
                      <td className="num">{l.quantity}</td>
                      <td className="num">{money(l.unitPrice)}</td>
                      <td className="num">{l.discount ? `${l.discount}%` : "—"}</td>
                      <td className="num">{l.taxPercent ? `${l.taxPercent}%` : "—"}</td>
                      <td className="num">{money(l.lineTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>

            <Section title="Payments">
              {(inv.paymentHistory || []).length === 0 ? <span className="lab-muted">No payments yet.</span> : (
                <table className="lab-lines">
                  <thead><tr><th>Date</th><th>Method</th><th>Note</th><th>By</th><th className="num">Amount</th><th /></tr></thead>
                  <tbody>
                    {inv.paymentHistory.map((p) => (
                      <tr key={p._id} style={p.voided ? { opacity: .5, textDecoration: "line-through" } : undefined}>
                        <td>{fmtDateTime(p.date)}</td>
                        <td>{p.paymentMethod}</td>
                        <td>{p.voided ? <span className="lab-danger-text">Voided: {p.voidReason}</span> : p.note || "—"}</td>
                        <td>{p.recordedBy?.name || "—"}</td>
                        <td className="num">{money(p.amount)}</td>
                        <td>{admin && !p.voided && inv.status !== "Cancelled" && <Button size="xs" variant="ghost" onClick={() => setReason({ kind: "void", paymentId: p._id })}>Void</Button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>
            {inv.cancellation?.reason && <div className="lab-danger-text">Cancelled: {inv.cancellation.reason}</div>}
          </>
        )}
      </Modal>

      <PaymentModal open={payOpen} onClose={() => setPayOpen(false)} invoice={inv} onSaved={after} />
      <ReasonModal
        open={!!reason}
        title={reason?.kind === "void" ? "Void payment" : "Cancel invoice"}
        onClose={() => setReason(null)}
        busy={busy}
        onSubmit={doReason}
      />
    </>
  );
};

export default InvoiceDetail;
